import { OppButton, OppInput } from '../components/ui/control';
import { useState, useMemo, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { useSharedAuth } from '@litopia/shared';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useMyLetters, useShareLetter, useLetterShareCount, useLetterThread, useLetterPhotos, useUploadLetterPhotoFile, useLetterPhotoSignedUrl, useAllMarkedLetters, useToggleLetterMark, useUpdateLetterStatus } from '../hooks/useOpp';
import type { OppLetter, OppLetterStatus } from '@/types/opp';
import { Mail, Send, FileText, Star, Search, MapPin, Clock, Archive, Inbox, X, Activity, Reply, Share2, Image, MessageSquare, Upload } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useCopy } from '../copy';
import { LetterCardSkeleton } from '../components/ui/Skeleton';
import LetterStatusTracker from '../components/LetterStatusTracker';
import { SemanticState } from '../components/semantic/SemanticState';
import { SEARCH_DEBOUNCE_DELAY_MS } from '../lib/constants';
import { useOppQueryClient } from '../lib/queryRuntime';

type Tab = 'inbox' | 'sent' | 'drafts' | 'starred' | 'archived';

const STATUS_CLASS: Partial<Record<OppLetterStatus, string>> = {
  draft: 'status-draft',
  generated: 'status-generated',
  collected: 'status-collected',
  in_transit: 'status-in-transit',
  delivered: 'status-delivered',
  read: 'status-read',
} as const;

export default function LettersPage() {
  const { user, loading } = useSharedAuth();
  const { t } = useCopy();
  const navigate = useNavigate();
  const queryClient = useOppQueryClient();
  const [tab, setTab] = useState<Tab>('inbox');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), SEARCH_DEBOUNCE_DELAY_MS);
  }, []);
  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);

  const shareLetter = useShareLetter();
  const { data: starredLetters } = useAllMarkedLetters('starred');
  const { data: archivedLetters } = useAllMarkedLetters('archived');
  const toggleMark = useToggleLetterMark();
  const updateLetterStatus = useUpdateLetterStatus();

  const toggleStar = useCallback((letterId: string) => {
    const isCurrentlyStarred = starredLetters?.some((l) => l.id === letterId) ?? false;

    toggleMark.mutate(
      { letterId, field: 'is_starred', value: !isCurrentlyStarred },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['opp_marked_letters', user?.id, 'starred'] });
        },
        onError: () => {
          toast.error(t('letters.action.saveFailed'));
        },
      }
    );
  }, [starredLetters, toggleMark, queryClient, user?.id, t]);

  const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'inbox', label: t('letters.tab.inbox'), icon: Inbox },
    { id: 'sent', label: t('letters.tab.sent'), icon: Send },
    { id: 'drafts', label: t('letters.tab.drafts'), icon: FileText },
    { id: 'starred', label: t('letters.tab.starred'), icon: Star },
    { id: 'archived', label: t('letters.tab.archived'), icon: Archive },
  ];

  // Tabs inbox/sent/drafts map to API; starred/archived use server marks
  const apiTab: 'inbox' | 'sent' | 'drafts' =
    tab === 'inbox' ? 'inbox' : tab === 'sent' ? 'sent' : 'drafts';
  const { data: letters, isLoading, isError, refetch } = useMyLetters(apiTab);

  const filtered = useMemo(() => {
    if (!debouncedSearch || !letters) return letters;
    const q = debouncedSearch.toLowerCase();
    return letters.filter((l) => l.title?.toLowerCase().includes(q) || l.content.toLowerCase().includes(q));
  }, [letters, debouncedSearch]);

  const filteredStarred = useMemo(() => {
    if (!debouncedSearch || !starredLetters) return starredLetters;
    const q = debouncedSearch.toLowerCase();
    return starredLetters.filter((l) => l.title?.toLowerCase().includes(q) || l.content.toLowerCase().includes(q));
  }, [starredLetters, debouncedSearch]);

  const filteredArchived = useMemo(() => {
    if (!debouncedSearch || !archivedLetters) return archivedLetters;
    const q = debouncedSearch.toLowerCase();
    return archivedLetters.filter((l) => l.title?.toLowerCase().includes(q) || l.content.toLowerCase().includes(q));
  }, [archivedLetters, debouncedSearch]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const selectLetter = useCallback((letter: OppLetter) => {
    setSelectedLetterId(selectedLetterId === letter.id ? null : letter.id);
    if (tab === 'inbox' && selectedLetterId !== letter.id && letter.status === 'delivered' && !updateLetterStatus.isPending) {
      updateLetterStatus.mutate({ id: letter.id, status: 'read' });
    }
  }, [selectedLetterId, tab, updateLetterStatus]);

  // Pre-sanitize all visible letter content to avoid repeated DOMPurify calls in render
  const sanitizedContent = useMemo(() => {
    const allLetters = [
      ...(filtered ?? []),
      ...(filteredStarred ?? []),
      ...(filteredArchived ?? []),
    ];
    return new Map(
      allLetters.map((l) => [l.id, DOMPurify.sanitize(l.content || '', { ALLOWED_TAGS: [] })])
    );
  }, [filtered, filteredStarred, filteredArchived]);

  if (loading) return <LetterCardSkeleton />;
  if (!user) return <Navigate to="/login" />;

  const handleShare = async (letterId: string) => {
    const url = `${window.location.origin}/letters/${letterId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'OpenPenPal Letter', url });
        shareLetter.mutate({ letterId, platform: 'native' });
      } catch {
        // User cancelled share
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        shareLetter.mutate({ letterId, platform: 'link' });
        toast.success(t('letters.share.copied'));
      } catch {
        toast.error(t('letters.share.copyFailed'));
      }
    }
  };

  return (
    <div className="space-y-4 pb-8">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--opp-muted))]" />
        <OppInput
          type="text"
          value={searchInput}
          onChange={handleSearchChange}
          placeholder={t('letters.search.placeholder')}
          className="w-full rounded-full border-2 border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))] py-2.5 pl-10 pr-4 text-sm text-[hsl(var(--opp-ink))] placeholder:text-[hsl(var(--opp-muted))] focus:border-[hsl(var(--ring))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent-soft))] transition-colors"
        />
      </div>

      {/* Tabs — sticky */}
      <div className="sticky top-14 z-10 flex gap-1 overflow-x-auto rounded-xl bg-[hsl(var(--opp-accent-soft))] p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <OppButton
              key={t.id}
              onClick={() => setTab(t.id)}
              aria-label={t.label}
              aria-pressed={tab === t.id}
              className={`flex min-h-11 flex-1 min-w-fit items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-xs font-semibold transition-all whitespace-nowrap ${
                tab === t.id
                  ? 'bg-[hsl(var(--opp-surface))] text-[hsl(var(--opp-ink))] shadow-sm'
                  : 'text-[hsl(var(--opp-accent))] hover:text-[hsl(var(--opp-accent-strong))]'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{t.label}</span>
            </OppButton>
          );
        })}
      </div>

      {/* Starred letters */}
      {tab === 'starred' && (
        <>
          {!filteredStarred || filteredStarred.length === 0 ? (
            <SemanticState kind={debouncedSearch ? 'no-results' : 'empty'} compact illustration={debouncedSearch ? undefined : 'empty-starred'} title={debouncedSearch ? '没有找到匹配的星标信件' : t('letters.empty.starred')} description={debouncedSearch ? '尝试更换关键词，或清除搜索后查看全部星标信件。' : t('letters.empty.starred.desc')} action={debouncedSearch ? <OppButton onClick={clearSearch} variant="outline" className="min-h-11 rounded-full px-4 text-xs font-semibold">清除搜索</OppButton> : undefined} />
          ) : (
            <div className="divide-y divide-[hsl(var(--opp-border))] overflow-hidden rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))]">
              {filteredStarred.map((letter) => {
                const isUnread = letter.status !== 'read';
                return (
                  <div
                    key={letter.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectLetter(letter)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectLetter(letter); } }}
                    className={`flex cursor-pointer items-start gap-3 p-3 transition-colors hover:bg-[hsl(var(--opp-hero))] sm:p-4 border-l-4 border-transparent`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))]">
                      <Mail className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className={`truncate font-serif text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-[hsl(var(--opp-ink))]`}>
                          {letter.title || t('letters.untitled')}
                        </h3>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className={`rounded-full px-2 py-0.5 text-caption font-semibold ${STATUS_CLASS[letter.status] ?? 'status-draft'}`}>
                            {t(`letters.status.${letter.status}` as never) || letter.status}
                          </span>
                          <OppButton
                            onClick={(e) => { e.stopPropagation(); toggleStar(letter.id); }}
                            aria-label="取消星标"
                            className="min-h-11 min-w-11 flex items-center justify-center transition-colors text-[hsl(var(--opp-accent))]"
                          >
                            <Star className="h-3.5 w-3.5" fill="currentColor" />
                          </OppButton>
                        </div>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[hsl(var(--opp-accent-strong))]">{sanitizedContent.get(letter.id) || ''}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-caption text-[hsl(var(--opp-muted))]">
                        {letter.recipient_op_code && (
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{letter.recipient_op_code}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(letter.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {selectedLetterId && filteredStarred?.some((l) => l.id === selectedLetterId) && (
            <div className="letter-card rounded-2xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                  <span className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('letters.statusTracker')}</span>
                </div>
                <OppButton aria-label="关闭状态追踪" onClick={() => setSelectedLetterId(null)} className="min-h-11 min-w-11 rounded-full p-2 text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-accent-soft))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors">
                  <X className="h-4 w-4" />
                </OppButton>
              </div>
              <LetterReadingPane letter={filteredStarred.find((letter) => letter.id === selectedLetterId)} />
              <LetterStatusTracker letterId={selectedLetterId} />
              <LetterDetailExtras letterId={selectedLetterId} />
            </div>
          )}
        </>
      )}

      {/* Archived letters */}
      {tab === 'archived' && (
        <>
          {!filteredArchived || filteredArchived.length === 0 ? (
            <SemanticState kind={debouncedSearch ? 'no-results' : 'empty'} compact illustration={debouncedSearch ? undefined : 'empty-archive'} title={debouncedSearch ? '没有找到匹配的归档信件' : t('letters.empty.archived')} description={debouncedSearch ? '尝试更换关键词，或清除搜索后查看全部归档信件。' : t('letters.empty.archived.desc')} action={debouncedSearch ? <OppButton onClick={clearSearch} variant="outline" className="min-h-11 rounded-full px-4 text-xs font-semibold">清除搜索</OppButton> : undefined} />
          ) : (
            <div className="divide-y divide-[hsl(var(--opp-border))] overflow-hidden rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))]">
              {filteredArchived.map((letter) => {
                const isUnread = letter.status !== 'read';
                return (
                  <div
                    key={letter.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectLetter(letter)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectLetter(letter); } }}
                    className={`flex cursor-pointer items-start gap-3 p-3 transition-colors hover:bg-[hsl(var(--opp-hero))] sm:p-4 border-l-4 border-transparent`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))]">
                      <Mail className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className={`truncate font-serif text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-[hsl(var(--opp-ink))]`}>
                          {letter.title || t('letters.untitled')}
                        </h3>
                        <span className={`rounded-full px-2 py-0.5 text-caption font-semibold ${STATUS_CLASS[letter.status] ?? 'status-draft'}`}>
                          {t(`letters.status.${letter.status}` as never) || letter.status}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[hsl(var(--opp-accent-strong))]">{sanitizedContent.get(letter.id) || ''}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-caption text-[hsl(var(--opp-muted))]">
                        {letter.recipient_op_code && (
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{letter.recipient_op_code}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(letter.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {selectedLetterId && filteredArchived?.some((l) => l.id === selectedLetterId) && (
            <div className="letter-card rounded-2xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                  <span className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('letters.statusTracker')}</span>
                </div>
                <OppButton
                  onClick={() => setSelectedLetterId(null)}
                  aria-label="关闭归档信件详情"
                  className="flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-accent-soft))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors"
                >
                  <X className="h-4 w-4" />
                </OppButton>
              </div>
              <LetterReadingPane letter={filteredArchived.find((letter) => letter.id === selectedLetterId)} />
              <LetterStatusTracker letterId={selectedLetterId} />
              <LetterDetailExtras letterId={selectedLetterId} />
            </div>
          )}
        </>
      )}

      {/* Letter list */}
      {tab !== 'starred' && tab !== 'archived' && (
        <>
          {isLoading ? (
            <div className="space-y-3">
              <LetterCardSkeleton />
              <LetterCardSkeleton />
              <LetterCardSkeleton />
            </div>
          ) : isError ? (
            <SemanticState
              kind="error"
              illustration="state-error-network"
              title={t('letters.error.loadFailed')}
              description={t('letters.error.networkError')}
              action={<OppButton onClick={() => refetch()} variant="primary" className="min-h-11 rounded-full px-5 py-2 text-sm font-semibold">{t('letters.error.retry')}</OppButton>}
            />
          ) : filtered && filtered.length > 0 ? (
            <>
            {/* Stats bar */}
            <div className="px-1 text-xs text-[hsl(var(--opp-accent))]">
              {t('letters.count').replace('%s', String(filtered.length))}
              {tab === 'inbox' && (() => {
                const unread = filtered.filter((l) => l.status !== 'read').length;
                return unread > 0 ? <span className="ml-1">· <span className="font-semibold text-[hsl(var(--opp-accent-strong))]">{t('letters.unread').replace('%s', String(unread))}</span></span> : null;
              })()}
            </div>

            <div className="divide-y divide-[hsl(var(--opp-border))] overflow-hidden rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))]">
              {filtered.map((letter) => {
                const isUnread = tab === 'inbox' && letter.status !== 'read';
                return (
                  <div
                    key={letter.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectLetter(letter)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectLetter(letter); } }}
                    className={`flex cursor-pointer items-start gap-3 p-3 transition-colors hover:bg-[hsl(var(--opp-hero))] sm:p-4 ${
                      isUnread ? 'border-l-4 border-[hsl(var(--ring))]' : 'border-l-4 border-transparent'
                    }`}
                  >
                    {/* Mail avatar */}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))]">
                      <Mail className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className={`truncate font-serif text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-[hsl(var(--opp-ink))]`}>
                          {letter.title || t('letters.untitled')}
                        </h3>
                        <div className="flex items-center gap-1 shrink-0">
                          <span
                            className={`rounded-full px-2 py-0.5 text-caption font-semibold ${
                              STATUS_CLASS[letter.status] ?? 'status-draft'
                            }`}
                          >
                            {t(`letters.status.${letter.status}` as never) || letter.status}
                          </span>
                          <OppButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleStar(letter.id);
                            }}
                            aria-label={starredLetters?.some((l) => l.id === letter.id) ? '取消星标' : '添加星标'}
                            className={`min-h-11 min-w-11 flex items-center justify-center transition-colors ${
                              starredLetters?.some((l) => l.id === letter.id) ? 'text-[hsl(var(--opp-accent))]' : 'text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-muted))]'
                            }`}
                          >
                            <Star
                              className="h-3.5 w-3.5"
                              fill={starredLetters?.some((l) => l.id === letter.id) ? 'currentColor' : 'none'}
                            />
                          </OppButton>
                        </div>
                      </div>

                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[hsl(var(--opp-accent-strong))]">
                        {sanitizedContent.get(letter.id) || ''}
                      </p>

                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-caption text-[hsl(var(--opp-muted))]">
                        {letter.recipient_op_code && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {letter.recipient_op_code}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(letter.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Status tracker panel */}
            {selectedLetterId && (
              <div className="letter-card rounded-2xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                    <span className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('letters.statusTracker')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {tab === 'inbox' && filtered?.find((l) => l.id === selectedLetterId)?.recipient_id === user?.id && (
                      <OppButton
                        onClick={() => navigate(`/write?replyTo=${selectedLetterId}`)}
                        className="flex min-h-11 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))] transition-colors"
                      >
                        <Reply className="h-3.5 w-3.5" />
                        {t('letters.action.reply')}
                      </OppButton>
                    )}
                    <OppButton
                      onClick={() => handleShare(selectedLetterId)}
                      className="flex min-h-11 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))] transition-colors"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      {t('letters.action.share')}
                    </OppButton>
                    <OppButton
                      onClick={() => setSelectedLetterId(null)}
                      aria-label="关闭状态追踪"
                      className="min-h-11 min-w-11 rounded-full p-2 text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-accent-soft))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </OppButton>
                  </div>
                </div>
                <LetterReadingPane letter={filtered?.find((letter) => letter.id === selectedLetterId)} />
                <LetterStatusTracker letterId={selectedLetterId} />
                <LetterDetailExtras letterId={selectedLetterId} />
              </div>
            )}
            </>
          ) : (
            <SemanticState
              kind={debouncedSearch ? 'no-results' : 'empty'}
              illustration={debouncedSearch ? undefined : (tab === 'inbox' ? 'empty-inbox' : tab === 'sent' ? 'empty-sent' : 'empty-draft')}
              title={debouncedSearch ? '没有找到匹配的信件' : (tab === 'inbox' ? t('letters.empty.inbox') : tab === 'sent' ? t('letters.empty.sent') : t('letters.empty.drafts'))}
              description={debouncedSearch ? '尝试更换关键词，或清除搜索后查看全部信件。' : (tab === 'inbox' ? t('letters.empty.inbox.desc') : tab === 'sent' ? t('letters.empty.sent.desc') : t('letters.empty.drafts.desc'))}
              action={debouncedSearch ? <OppButton onClick={clearSearch} variant="outline" className="min-h-11 rounded-full px-4 text-xs font-semibold">清除搜索</OppButton> : <Link to="/write" className="opp-vs-button opp-vs-button--primary">{t('letters.writeNew')}</Link>}
            />
          )}
        </>
      )}

      {/* Drift conversations shortcut */}
      <Link
        to="/drift"
        className="letter-card flex items-center gap-3 p-3 transition-colors hover:bg-[hsl(var(--opp-hero))]"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-feedback-info/10">
          <Activity className="h-4 w-4 text-on-image" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('letters.drift.title')}</p>
          <p className="text-caption text-[hsl(var(--opp-accent))]">{t('letters.drift.desc')}</p>
        </div>
        <span className="text-[hsl(var(--opp-muted))] text-xs">→</span>
      </Link>
    </div>
  );
}

/** Renders a single letter photo using a signed URL (avoids calling hook in a map loop) */
function LetterPhotoItem({ photo }: { photo: { id: string; image_url: string } }) {
  const { t } = useCopy();
  const { data: signedUrl, isLoading, isError } = useLetterPhotoSignedUrl(photo.image_url);
  if (isLoading) {
    return <div className="h-20 w-20 shrink-0 rounded-lg bg-[hsl(var(--opp-accent-soft))] animate-pulse border border-[hsl(var(--opp-border))]" />;
  }
  if (isError || !signedUrl) {
    return (
      <div className="h-20 w-20 shrink-0 rounded-lg bg-[hsl(var(--opp-accent-soft))] border border-[hsl(var(--opp-border))] flex items-center justify-center">
        <span className="text-caption text-[hsl(var(--opp-accent))] text-center px-1">{t('letters.photo.loadFailed')}</span>
      </div>
    );
  }
  return (
    <img
      src={signedUrl}
      alt=""
      className="h-20 w-20 shrink-0 rounded-lg object-cover border border-[hsl(var(--opp-border))]"
    />
  );
}

/** Shows thread info, share count, and photos for a selected letter */
function LetterReadingPane({ letter }: { letter?: OppLetter }) {
  const { t } = useCopy();
  if (!letter) return null;
  const safeContent = DOMPurify.sanitize(letter.content || '', {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
  return (
    <article aria-labelledby={`letter-reading-${letter.id}`} className="rounded-2xl bg-[hsl(var(--opp-surface-raised))] px-4 py-5 sm:px-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-caption uppercase tracking-wider text-[hsl(var(--opp-muted))]">信件内容</p>
          <h3 id={`letter-reading-${letter.id}`} className="mt-1 font-serif text-lg font-semibold text-[hsl(var(--opp-ink))]">{letter.title || '无标题信件'}</h3>
        </div>
        <span className={`rounded-full px-2 py-1 text-caption font-semibold ${STATUS_CLASS[letter.status] ?? 'status-draft'}`}>{t(`letters.status.${letter.status}` as never) || letter.status}</span>
      </div>
      <div className="opp-letter-reading prose prose-amber max-w-none font-serif leading-8 text-[hsl(var(--opp-ink))]" dangerouslySetInnerHTML={{ __html: safeContent }} />
    </article>
  );
}

function LetterDetailExtras({ letterId }: { letterId: string }) {
  const { t } = useCopy();
  const { data: thread } = useLetterThread(letterId);
  const { data: photos } = useLetterPhotos(letterId);
  const { data: shareCount } = useLetterShareCount(letterId);
  const uploadPhotoFile = useUploadLetterPhotoFile();
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    uploadPhotoFile.mutate(
      { letterId, file, isPublic: false },
      {
        onSettled: () => setUploading(false),
        onError: (err) => toast.error(err instanceof Error ? err.message : t('letters.photo.uploadFailed')),
      }
    );
  };

  const hasContent = thread || (photos && photos.length > 0) || (shareCount && shareCount > 0);
  if (!hasContent) return null;

  return (
    <div className="space-y-3 border-t border-[hsl(var(--opp-border))] pt-3">
      {/* Share count */}
      {shareCount != null && shareCount > 0 && (
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--opp-accent-strong))]">
          <Share2 className="h-3.5 w-3.5 text-[hsl(var(--opp-accent))]" />
          <span>{t('letters.share.count').replace('%s', String(shareCount))}</span>
        </div>
      )}

      {/* Thread info */}
      {thread && (
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--opp-accent-strong))]">
          <MessageSquare className="h-3.5 w-3.5 text-[hsl(var(--opp-accent))]" />
          <span>
            {t('letters.thread.count').replace('%s', String(thread.reply_count ?? 0))}
            {thread.last_reply_at && (
              <span className="ml-1 text-[hsl(var(--opp-muted))]">
                · {t('letters.thread.lastReply')} {formatDistanceToNow(new Date(thread.last_reply_at), { addSuffix: true })}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Photos gallery + upload */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--opp-accent-strong))]">
            <Image className="h-3.5 w-3.5 text-[hsl(var(--opp-accent))]" />
            <span>{t('letters.photo.title')}{photos && photos.length > 0 ? ` (${photos.length})` : ''}</span>
          </div>
          <label className="flex items-center gap-1 cursor-pointer rounded-md bg-[hsl(var(--opp-hero))] border border-[hsl(var(--opp-border))] px-2 py-1 text-caption font-medium text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))] transition-colors">
            <Upload className="h-3 w-3" />
            {uploading ? t('letters.photo.uploading') : t('letters.photo.upload')}
            <OppInput type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
        {photos && photos.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((photo: { id: string; image_url: string }) => (
              <LetterPhotoItem key={photo.id} photo={photo} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
